// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import useIsPackageAvailableFrom from '@hooks/queries/settings/useIsPackageAvailableFrom';
import React from 'react';
import FormDialog from '../../components/Dialog/FormDialog';
import TextInput from '../../components/Form/TextInput';
import { useParties } from '../../contexts/PartiesContext';
import { fmt } from '../../utils/common';
import { DialogProps } from './DialogProps';

export const ModifyCommercialAgreementDialog: React.FC<
  DialogProps & {
    currFeeReceiver: string;
    currUtilityFees: string;
    currBillingPeriodMinutes?: string;
    currRate?: string;
    currRewardReceiver?: string;
    onModify:
      | ((
          feeReceiver: string,
          utilityFees: string,
          currBillingPeriodMinutes?: string,
          currRate?: string,
          rewardReceiver?: string,
        ) => Promise<unknown>)
      | undefined;
  }
> = ({
  open,
  onClose,
  onModify,
  currFeeReceiver,
  currUtilityFees,
  currBillingPeriodMinutes,
  currRate,
  currRewardReceiver,
}) => {
  const { party, operator } = useParties();
  const enableRewardReceiverConfig = useIsPackageAvailableFrom(
    'utility-commercials-v0',
    [party, operator],
    '0.3.0',
  );
  const [feeReceiver, setFeeReceiver] = React.useState(currFeeReceiver);
  const [utilityFees, setUtilityFees] = React.useState(fmt(currUtilityFees, 2));
  const [billingPeriodMinutes, setBillingPeriodMinutes] = React.useState(
    currBillingPeriodMinutes || '',
  );
  const [rate, setRatePerDay] = React.useState(currRate ? fmt(currRate, 2) : '');
  const [rewardReceiver, setRewardReceiver] = React.useState(currRewardReceiver || '');
  const handleClose = React.useCallback(() => {
    setFeeReceiver('');
    setUtilityFees('');
    setRewardReceiver('');
    onClose();
  }, [onClose]);

  const handleSubmit = React.useCallback(() => {
    if (onModify) {
      return onModify(feeReceiver, utilityFees, billingPeriodMinutes, rate, rewardReceiver).finally(
        () => {
          handleClose();
        },
      );
    }
    handleClose();
    return Promise.resolve();
  }, [handleClose, onModify, feeReceiver, utilityFees, billingPeriodMinutes, rate, rewardReceiver]);
  return (
    <FormDialog
      open={open}
      title="Modify Commercial Agreement"
      onCancel={onClose}
      onSubmit={handleSubmit}
      submitName="Modify"
    >
      <TextInput
        enableTopLabel
        required
        label="Fee Receiver"
        value={feeReceiver}
        setValue={setFeeReceiver}
      />
      {enableRewardReceiverConfig.data && (
        <TextInput
          enableTopLabel
          required
          label="Reward Receiver"
          value={rewardReceiver}
          setValue={setRewardReceiver}
        />
      )}
      <TextInput
        required
        enableTopLabel
        label="Credential Billing Fee (USD)"
        value={utilityFees}
        setValue={setUtilityFees}
      />
      <TextInput
        required={billingPeriodMinutes !== ''}
        enableTopLabel
        label="Fee per day (USD)"
        value={rate || ''}
        setValue={setRatePerDay}
      />
      <TextInput
        enableTopLabel
        required={rate !== ''}
        label="Billing Period (minutes)"
        value={billingPeriodMinutes || ''}
        setValue={setBillingPeriodMinutes}
      />
    </FormDialog>
  );
};
